import { apiClient } from '@/lib/api-client';
import type { ModifierGroup } from '@bbpos/ui/src/components/RestaurantComponents/ItemModifierPanel/types';

// Raw shape returned by GET /modifiers (see modifiers.repository.findAll).
interface RawModifierOption {
  id: string;
  name: string;
  price_adjustment: number;
}
interface RawModifierGroup {
  id: string;
  name: string;
  required: boolean;
  max_selections: number | null;
  options: RawModifierOption[];
}

function toModifierGroups(groups: RawModifierGroup[]): ModifierGroup[] {
  return groups
    .filter((g) => g.options.length > 0)
    .map((g) => ({
      id: g.id,
      name: g.name,
      required: g.required,
      multiSelect: g.max_selections !== 1,
      options: g.options.map((o) => ({
        id: o.id,
        name: o.name,
        additionalPrice: o.price_adjustment,
      })),
    }));
}

/**
 * Fetch the modifier groups applicable to a product, mapped to the shape the
 * shared ItemModifierPanel expects. Groups with no options are dropped (nothing
 * to suggest). `multiSelect` is derived from max_selections (1 ⇒ single-select).
 */
export const modifierService = {
  async getForProduct(productId: string, locationId?: string): Promise<ModifierGroup[]> {
    const qs = new URLSearchParams({ product_id: productId, limit: '100' });
    if (locationId) qs.set('location_id', locationId);
    const res = await apiClient<{ data: RawModifierGroup[] }>(`/modifiers?${qs.toString()}`);
    return toModifierGroups(res.data ?? []);
  },

  /**
   * Fetch every modifier group for a location along with the products each one
   * applies to. The menu grid needs this up front so it can mark which items are
   * customisable without issuing a request per card. A group with no product
   * links applies to every product at that location (see modifiers.repository
   * `findAll`), which is why `productIds` may be empty.
   */
  async getForLocation(
    locationId?: string,
  ): Promise<{ group: ModifierGroup; productIds: string[] }[]> {
    const qs = new URLSearchParams({ limit: '100' });
    if (locationId) qs.set('location_id', locationId);
    const res = await apiClient<{ data: (RawModifierGroup & { product_ids?: string[] })[] }>(
      `/modifiers?${qs.toString()}`,
    );
    const raw = res.data ?? [];
    const byId = new Map(raw.map((g) => [g.id, g.product_ids ?? []]));
    return toModifierGroups(raw).map((group) => ({
      group,
      productIds: byId.get(group.id) ?? [],
    }));
  },
};
